import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../../i18n";
import { Footer } from "./Footer";

const MiniLangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-[var(--border)] bg-white p-0.5 text-[11px] font-semibold" data-testid="legal-language-toggle">
      {["de", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`legal-language-toggle-${l}`}
          className={`rounded-full px-2.5 py-1 transition-colors ${lang === l ? "bg-[var(--ink)] text-white" : "text-slate-400 hover:text-[var(--ink)]"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

/** Shared layout for legal pages (Impressum, Datenschutz). */
export const LegalLayout = ({ title, intro, updated, children }) => {
  const { lang } = useLang();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="sticky top-0 z-40 border-b border-[var(--border)] bg-white/85 backdrop-blur">
        <div className="mx-auto flex max-w-[820px] items-center justify-between px-4 py-3.5 sm:px-6">
          <Link to="/" data-testid="legal-wordmark" className="font-heading text-[20px] font-extrabold text-[var(--ink)]">
            Procur<span className="text-[var(--blue)]">mo</span>
          </Link>
          <div className="flex items-center gap-3">
            <MiniLangToggle />
            <Link
              to="/"
              data-testid="legal-back-link"
              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--slate)] transition-colors hover:text-[var(--blue)]"
            >
              <ArrowLeft className="h-4 w-4" />
              {lang === "de" ? "Zurück" : "Back"}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[820px] px-4 py-12 sm:px-6 lg:py-16">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-[var(--ink)] sm:text-4xl">{title}</h1>
        {intro && <p className="mt-4 text-[15px] leading-relaxed text-[var(--slate)]">{intro}</p>}
        {updated && <p className="mt-3 text-xs text-slate-400">{updated}</p>}
        <div className="mt-10 space-y-9">{children}</div>
      </main>

      <Footer />
    </div>
  );
};

/** A titled section that renders paragraphs and an optional bullet list. */
export const LegalSection = ({ heading, paragraphs = [], list = [], children }) => (
  <section>
    {heading && <h2 className="font-heading text-lg font-semibold text-[var(--ink)]">{heading}</h2>}
    <div className="mt-2.5 space-y-2.5 text-[15px] leading-relaxed text-[var(--slate)]">
      {paragraphs.map((p, i) => (
        <p key={`${i}-${String(p).slice(0, 24)}`}>{p}</p>
      ))}
      {list.length > 0 && (
        <ul className="ml-5 list-disc space-y-1.5 marker:text-[var(--blue)]">
          {list.map((li, i) => (
            <li key={`${i}-${String(li).slice(0, 24)}`}>{li}</li>
          ))}
        </ul>
      )}
      {children}
    </div>
  </section>
);

/** A soft note box for placeholders / legal disclaimers. */
export const LegalNote = ({ children }) => (
  <div className="rounded-xl border border-[var(--border)] bg-[var(--blue-soft)]/50 px-4 py-3 text-[13px] leading-relaxed text-[var(--ink)]/80">
    {children}
  </div>
);
